import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Button, Pager, useAlert, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Title } from "capitalrx-components-2.0/lib/typography";

import { financeGetProviderPaymentsList, getProviderManagementAPI } from "@/api";
import { Finance, ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext } from "@/context";
import { BackLink } from "@/components";
import { PaymentDropdown } from "./PaymentDropdown";
import { Filter } from "./Filter";
import type { UiSortDirection } from "./PaymentDropdown";

export type DataRow<T> = {
  id: number | string;
  data: T;
};

type SectionRow = {
  id: string;
  label: string;
  isUpcoming: boolean;
  payment_date?: string;
  result_rows: ReadonlyArray<DataRow<Finance.MedicalClaimProviderReportList>>;
  total_results: number;
  transaction?: Finance.MedicalPaymentTransactionSchema[];
};

type ApiSortDirection = "asc" | "desc";
type SortField = "start_date" | "patient_name" | "submission_date" | "claim_status";

export type ApiOrderBy = Array<Record<string, ApiSortDirection>>;

export function buildProviderPaymentsPayload(args: {
  pageNumber: number;
  resultsPerPage: number;
  filters: Record<string, any>;
  order_by?: ApiOrderBy;
}) {
  const { pageNumber, resultsPerPage, filters, order_by } = args;

  return {
    page_number: pageNumber,
    results_per_page: resultsPerPage,
    ...filters,
    ...(order_by?.length ? { order_by } : {}),
  };
}


export const MedicalClaimsProviderList: FC = () => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();

  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const [loadingProvider, setLoadingProvider] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const [upcoming, setUpcoming] = useState<Finance.MedicalClaimProviderReportList[]>([]);
  const [groupedByDate, setGroupedByDate] = useState<
    Record<string, Finance.MedicalClaimProviderReportList[]>
  >({});
  const [transactionsByDate, setTransactionsByDate] = useState<
    Record<string, Finance.MedicalPaymentTransactionSchema[]>
  >({});

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const RESULTS_PER_PAGE = 10;

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<UiSortDirection>(null);

  const handleSortChange = useCallback(
    (field: SortField) => {
      setPage(0);

      if (sortField !== field) {
        setSortField(field);
        setSortDirection("ascending");
        return;
      }

      setSortDirection((prev) => {
        if (prev === null) return "ascending";
        if (prev === "ascending") return "descending";
        return null;
      });
    },
    [sortField],
  );

  const apiSortDirection = useMemo<ApiSortDirection | undefined>(() => {
    if (sortDirection === "ascending") return "asc";
    if (sortDirection === "descending") return "desc";
    return undefined;
  }, [sortDirection]);

  const orderBy = useMemo<ApiOrderBy | undefined>(() => {
    if (!sortField || !apiSortDirection) return undefined;

    return [{ [sortField]: apiSortDirection } as Record<SortField, ApiSortDirection>];
  }, [sortField, apiSortDirection]);

  const getProvider = useCallback(async () => {
    setLoadingProvider(true);
    try {
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
    } catch (error) {
      console.error("ProviderManagement fetch error", error);
    } finally {
      setLoadingProvider(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    getProvider();
  }, [getProvider]);

  useEffect(() => {
    setPage(0);
  }, [provider]);

  const makePaymentDateKey = useCallback((backendDate?: string): string => {
    if (!backendDate) return "Unscheduled";
    const m = moment.utc(backendDate, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    if (!m.isValid()) return "Unscheduled";
    return m.format("YYYY-MM-DD");
  }, []);

  const fetchClaims = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        if (provider && !loadingProvider) {
          const payload = buildProviderPaymentsPayload({
            pageNumber,
            resultsPerPage: RESULTS_PER_PAGE,
            filters: activeFilters,
            order_by: orderBy,
          });

          const response: Finance.MedicalProviderPaymentsListResultSchema =
            await financeGetProviderPaymentsList(payload, getAuthToken());

          setUpcoming(response.results.upcoming_medical_claims_results ?? []);

          const transactions = (response.results.medical_payment_transactions ?? [])
            .map((t) => t.medical_payment_transaction)
            .filter(Boolean) as Finance.MedicalPaymentTransactionSchema[];

          const grouped: Record<string, Finance.MedicalClaimProviderReportList[]> = {};
          const txByDate: Record<string, Finance.MedicalPaymentTransactionSchema[]> = {};

          for (const tx of transactions) {
            const key = makePaymentDateKey(tx.settlement_date);
            const claims = tx.medical_claim_payment_results ?? [];

            grouped[key] = (grouped[key] ?? []).concat(claims);
            txByDate[key] = (txByDate[key] ?? []).concat(tx);
          }

          setGroupedByDate(grouped);
          setTransactionsByDate(txByDate);
        }
      } catch (error) {
        console.error("Failed to fetch claims:", error);
        showAlert({
          type: "error",
          style: "banner",
          message: "Failed to fetch claims",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      RESULTS_PER_PAGE,
      provider,
      loadingProvider,
      getAuthToken,
      makePaymentDateKey,
      activeFilters,
      orderBy,
      showAlert,
    ],
  );

  useEffect(() => {
    fetchClaims(page);
  }, [fetchClaims, page]);

  const sectionRows: ReadonlyArray<DataRow<SectionRow>> = useMemo(() => {
    const rows: DataRow<SectionRow>[] = [];

    if (upcoming.length > 0) {
      const upcomingRows = upcoming.map((item, index) => ({
        id: `upcoming-${index}`,
        data: item,
      }));

      rows.push({
        id: "upcoming",
        data: {
          id: "upcoming",
          label: "Upcoming",
          isUpcoming: true,
          payment_date: undefined,
          result_rows: upcomingRows,
          total_results: upcomingRows.length,
          transaction: undefined,
        },
      });
    }

    const issuedKeys = Object.keys(groupedByDate).sort((a, b) => {
      if (a === "Unscheduled") return 1;
      if (b === "Unscheduled") return -1;
      return moment(b, "YYYY-MM-DD").valueOf() - moment(a, "YYYY-MM-DD").valueOf();
    });

    for (const dateKey of issuedKeys) {
      const claimsForDate = groupedByDate[dateKey] ?? [];
      const resultRows = claimsForDate.map((item, idx) => ({
        id: `${dateKey}-${idx}`,
        data: item,
      }));

      const displayDate =
        dateKey === "Unscheduled"
          ? undefined
          : moment.utc(dateKey, "YYYY-MM-DD").format("MM/DD/YYYY");

      rows.push({
        id: dateKey,
        data: {
          id: dateKey,
          label: displayDate ?? "Unscheduled",
          isUpcoming: false,
          payment_date: displayDate,
          result_rows: resultRows,
          total_results: resultRows.length,
          transaction: transactionsByDate[dateKey] ?? [],
        },
      });
    }

    return rows;
  }, [upcoming, groupedByDate, transactionsByDate]);

  const sectionsDataSource = useSimpleTableDataSource<SectionRow>(sectionRows, { pageSize: 10 });

  useEffect(() => {
    sectionsDataSource.setFilter({ ...activeFilters });
  }, [activeFilters, sectionsDataSource]);

  const handleFilterClick = () => setIsDrawerOpen(true);
  const handleClickCancel = () => setIsDrawerOpen(false);
  const handleApplyFilters = (filters: Record<string, any>) => setActiveFilters(filters);

  return (
    <Stack spacing="large">
      <Stack spacing="small">
        <BackLink linkText="Home" />
        <Shelf>
          <Title>Medical Claims &amp; Payments</Title>
          <Shelf.Spacer />
          <Button
            appearance="secondary"
            icon="filter"
            onClick={handleFilterClick}
            disabled={sectionsDataSource.busy}
          >
            {`Filter (${filterCount})`}
          </Button>

          <Filter
            isOpen={isDrawerOpen}
            handleCancel={handleClickCancel}
            onFilterCountChange={setFilterCount}
            onApplyFilters={handleApplyFilters}
          />
        </Shelf>
      </Stack>

      <Stack spacing="medium-large">
        {sectionsDataSource.rows.length > 0 ? (
          sectionsDataSource.rows.map((row) => {
            const s = row.data;
            return (
              <PaymentDropdown
                key={s.id}
                isUpcoming={s.isUpcoming}
                payment_date={s.payment_date}
                result_rows={s.result_rows}
                total_results={s.total_results}
                transaction={s.transaction}
                loading={loading}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              />
            );
          })
        ) : (
          !loading && (
            <div style={{ padding: "8px 0" }}>
              No claims or payments found for this provider.
            </div>
          )
        )}

        {sectionRows.length > 0 && <Pager dataSource={sectionsDataSource} />}
      </Stack>
    </Stack>
  );
};
